import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, ScrollText, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/Button';
import { CornerBrackets, OrnamentalDivider } from '../ui/Decorations';
import { ThemeId, themes } from '../../data/quizData';
import { FaqAccordion, FaqItem } from '../ui/FaqAccordion';
const faqItems: FaqItem[] = [{
  question: '¿Por qué debería confiar en este sitio si acabo de conocerlo en un anuncio?',
  answer: 'La transparencia es nuestra base. El Oráculo Turco es una plataforma de análisis espiritual que utiliza protocolos de encriptación de grado bancario (SSL) para proteger tus datos. Más de 45,000 mujeres en toda Latinoamérica han pasado por este proceso técnico. No somos un perfil de redes sociales; somos un sistema profesional configurado para ofrecerte respuestas privadas, seguras y sin juicios.',
  icon: 'help'
}, {
  question: '¿Qué diferencia hay entre el tarot normal y el Oráculo Turco?',
  answer: 'El tarot convencional suele ser interpretativo y depende 100% de la intuición del lector. El Oráculo Turco utiliza triangulación cronológica. Esto significa que combinamos la sabiduría ancestral de las tablillas de Constantinopla con tus coordenadas de nacimiento exactas. No es una lectura genérica; es un cálculo preciso entre tu pasado y tus posibilidades futuras que elimina la ambigüedad del tarot común.',
  icon: 'help'
}, {
  question: '¿Cómo sé que esto no es una respuesta grabada o "copy-paste" para todo el mundo?',
  answer: 'Por la metodología de sincronización. Al inicio ingresaste datos únicos: tu nombre completo, fecha de nacimiento y signo. Esos datos actúan como filtros en nuestro algoritmo de lectura. Es matemáticamente imposible que recibas el mismo resultado que otra persona, porque nadie más tiene tu combinación exacta de tiempo, origen y la temática específica que elegiste consultar hoy.',
  icon: 'help'
}, {
  question: '¿Me va a llegar algo físico a mi casa o cómo recibo la lectura?',
  answer: 'Para tu total privacidad y rapidez, el acceso es 100% digital e inmediato. Tras confirmar tu pedido, el sistema genera tu reporte profesional en formato PDF de alta resolución que puedes abrir en tu celular o computadora. Sin esperas, sin paquetes que otros puedan ver en tu casa y con acceso de por vida para que lo consultes cada vez que necesites guía.',
  icon: 'help'
}, {
  question: '¿Qué pasa si no sé nada de tarot o espiritualidad? ¿Voy a entenderlo?',
  answer: 'Absolutamente. Hemos diseñado el reporte en un lenguaje claro, directo y accionable. No te daremos metáforas confusas. Recibirás un diagnóstico de tu situación actual y pasos concretos que el Oráculo sugiere para tu área de consulta. Es como tener una sesión con un consultor experto, pero escrita de forma sencilla para que puedas aplicarla desde el primer minuto.',
  icon: 'help'
}, {
  question: 'He probado otras lecturas y ninguna me ha funcionado, ¿por qué esta sí?',
  answer: 'La mayoría de las lecturas fallan porque son "horóscopos disfrazados". El Oráculo Turco se enfoca en la causalidad. No te decimos "qué te va a pasar" de forma pasiva, te revelamos los patrones de tu destino para que tú tomes el control. Si las otras lecturas fueron superficiales, es porque no tenían la profundidad técnica de la triangulación turca.',
  icon: 'help'
}, {
  question: '¿Es seguro pagar con mi tarjeta aquí?',
  answer: 'Totalmente. No almacenamos tus datos bancarios. Todos los pagos son procesados por las plataformas líderes a nivel mundial (Stripe / Hotmart / PayPal), que cuentan con los estándares de seguridad más altos de la industria. Es tan seguro como comprar en Amazon o pagar tu suscripción de Netflix.',
  icon: 'lock'
}, {
  question: '¿Qué pasa si leo mi resultado y no me siento identificada?',
  answer: 'Confiamos tanto en la precisión de nuestra triangulación que ofrecemos una Garantía de Satisfacción. Si después de leer tu análisis completo sientes que no resuena con tu realidad, tienes 7 días para solicitar el reembolso total de tu inversión. Nuestra misión no es solo vender un reporte, es entregarte la claridad que viniste a buscar.',
  icon: 'help'
}];
interface OfferPhaseProps {
  formData: {
    nombre_completo: string;
    theme: ThemeId;
    [key: string]: any;
  };
}
export const OfferPhase = ({
  formData
}: OfferPhaseProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const themeData = themes.find(t => t.id === formData.theme);
  const firstName = formData.nombre_completo.split(' ')[0] || 'Buscador';
  const handlePurchase = () => {
    setIsSubmitting(true);
    window.location.href = 'https://pay.hotmart.com/M105886331G?checkoutMode=10';
  };
  return <motion.div className="w-full max-w-4xl mx-auto px-4 py-12 flex flex-col min-h-screen" initial={{
    opacity: 0,
    y: 30
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 1
  }} data-id="element-61">
      <div className="text-center mb-10" data-id="element-62">
        <h1 className="font-cinzel mb-6 leading-tight" style={{
        color: '#D4AF37',
        fontSize: 'clamp(26px, 4.5vw, 46px)',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textShadow: '0 2px 12px rgba(212,175,55,0.25)'
      }} data-id="element-63">
          ANÁLISIS COMPLETADO: SE HA DETECTADO UNA CONFLUENCIA EN TU DESTINO
        </h1>

        <p className="font-sans max-w-3xl mx-auto" style={{
        color: '#F5F0E8',
        fontSize: '17px',
        lineHeight: 1.6,
        fontWeight: 400
      }} data-id="element-64">
          <span style={{
          color: '#D4AF37',
          fontWeight: 600
        }} data-id="element-65">
            {firstName}
          </span>
          , tu consulta técnica sobre{' '}
          <span style={{
          color: '#D4AF37',
          fontWeight: 600
        }} data-id="element-66">
            {themeData?.title}
          </span>{' '}
          ha revelado patrones que requieren tu atención inmediata.
        </p>
      </div>

      {/* ============ CREATOR / AUTHOR SECTION ============ */}
      <div className="mb-12" data-id="element-67">
        <div className="text-center mb-6" data-id="element-68">
          <h2 className="font-cinzel" style={{
          color: '#D4AF37',
          fontSize: 'clamp(22px, 3.5vw, 30px)',
          fontWeight: 600,
          letterSpacing: '0.04em'
        }} data-id="element-69">
            ¿Quién realiza el Tarot Turco?
          </h2>
          <div className="w-16 h-px bg-gold/50 mx-auto mt-4" data-id="element-70" />
        </div>

        <div className="relative rounded-sm overflow-hidden bg-black/50 backdrop-blur-sm" style={{
        border: '1px solid rgba(212,175,55,0.25)'
      }} data-id="element-71">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-50" data-id="element-72" />

          <div className="flex flex-col md:flex-row items-center gap-7 md:gap-9 p-6 md:p-9" data-id="element-73">
            {/* Portrait */}
            <div className="flex-shrink-0 relative" data-id="element-74">
              {/* Outer gold halo glow */}
              <div className="absolute -inset-3 rounded-full pointer-events-none" style={{
              background: 'radial-gradient(circle, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0) 70%)',
              filter: 'blur(10px)'
            }} data-id="element-75" />
              {/* Decorative outer ring */}
              <div className="absolute inset-0 rounded-full pointer-events-none" style={{
              border: '1px solid rgba(212,175,55,0.35)',
              transform: 'scale(1.09)'
            }} data-id="element-76" />
              <div className="relative w-56 h-56 md:w-72 md:h-72 rounded-full overflow-hidden" style={{
              border: '3px solid #D4AF37',
              boxShadow: '0 0 40px rgba(212,175,55,0.35), inset 0 0 0 1px rgba(0,0,0,0.5)'
            }} data-id="element-77">
                <img src="https://cdn.magicpatterns.com/uploads/hmAV23JQ7dfV3Mcg8j4rtd/9b293e3c-a918-4ef2-8c19-2ad9982c6755.png" alt="Hikmet Aydın, Maestro del Tarot Turco" className="w-full h-full object-cover" style={{
                objectPosition: '50% 25%'
              }} data-id="element-78" />
              </div>
            </div>

            {/* Bio */}
            <div className="flex-1 text-center md:text-left" data-id="element-79">
              <p className="font-sans uppercase mb-2" style={{
              color: '#b8923d',
              fontSize: '11px',
              letterSpacing: '0.25em',
              fontWeight: 500
            }} data-id="element-80">
                Maestro del Tarot Turco
              </p>
              <h3 className="font-cinzel mb-4" style={{
              color: '#D4AF37',
              fontSize: '26px',
              fontWeight: 600,
              letterSpacing: '0.02em'
            }} data-id="element-81">
                Hikmet Aydın
              </h3>
              <div className="space-y-3.5" data-id="element-82">
                <p className="font-sans" style={{
                color: '#E0D4BC',
                fontSize: '14.5px',
                lineHeight: 1.7,
                fontWeight: 400
              }} data-id="element-83">
                  Nacido bajo el cielo de Estambul, Kerem descubrió que podía
                  "leer" el peso del alma antes de aprender a leer los libros.
                  Lo que empezó como un don de la infancia heredado de sus
                  ancestros, se convirtió en el propósito de su vida.
                </p>
                <p className="font-sans" style={{
                color: '#E0D4BC',
                fontSize: '14.5px',
                lineHeight: 1.7,
                fontWeight: 400
              }} data-id="element-84">
                  Tras recorrer el mundo compartiendo su sabiduría, Kerem
                  encontró su hogar en la serenidad de El Salvador, donde hoy
                  vive rodeado de naturaleza. Desde allí, utiliza la tecnología
                  para llevar el Oráculo Turco a mujeres de todo el mundo,
                  hablando un español cálido y cercano que te hará sentir
                  escuchada desde la primera palabra.
                </p>
                <p className="font-sans" style={{
                color: '#E0D4BC',
                fontSize: '14.5px',
                lineHeight: 1.7,
                fontWeight: 400
              }} data-id="element-85">
                  Su filosofía es simple: las respuestas que buscas siempre han
                  estado ahí; Kerem solo te ayuda a verlas con la misma paz y
                  transparencia que se siente al disfrutar de un té en casa.
                  Kerem no se considera un mago, sino un puente entre lo que el
                  universo ya sabe y lo que tú necesitas comprender hoy.
                </p>
              </div>
            </div>
          </div>

          <div className="w-full h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-30" data-id="element-86" />
        </div>
      </div>

      {/* ============ ¿POR QUÉ EL TAROT TURCO ES DIFERENTE? ============ */}
      <div className="relative mb-16" data-id="element-87">
        {/* Outer luminous halo */}
        <div className="absolute -inset-2 rounded-sm pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.28) 0%, rgba(212,175,55,0) 70%)',
        filter: 'blur(14px)'
      }} aria-hidden="true" data-id="element-88" />

        <div className="relative rounded-sm overflow-hidden" style={{
        background: 'linear-gradient(135deg, rgba(212,175,55,0.10) 0%, rgba(92,26,27,0.18) 50%, rgba(212,175,55,0.08) 100%)',
        border: '1.5px solid #D4AF37',
        boxShadow: '0 0 40px rgba(212,175,55,0.35), inset 0 0 40px rgba(212,175,55,0.06)'
      }} data-id="element-89">
          {/* Top accent line */}
          <div className="absolute top-0 left-0 w-full h-[2px]" style={{
          background: 'linear-gradient(90deg, transparent 0%, #E5C97A 50%, transparent 100%)'
        }} data-id="element-90" />

          {/* Corner ornaments */}
          <span aria-hidden="true" className="absolute top-3 left-3 w-5 h-5" style={{
          borderTop: '1px solid #D4AF37',
          borderLeft: '1px solid #D4AF37'
        }} data-id="element-91" />
          <span aria-hidden="true" className="absolute top-3 right-3 w-5 h-5" style={{
          borderTop: '1px solid #D4AF37',
          borderRight: '1px solid #D4AF37'
        }} data-id="element-92" />
          <span aria-hidden="true" className="absolute bottom-3 left-3 w-5 h-5" style={{
          borderBottom: '1px solid #D4AF37',
          borderLeft: '1px solid #D4AF37'
        }} data-id="element-93" />
          <span aria-hidden="true" className="absolute bottom-3 right-3 w-5 h-5" style={{
          borderBottom: '1px solid #D4AF37',
          borderRight: '1px solid #D4AF37'
        }} data-id="element-94" />

          <div className="px-6 py-8 md:px-10 md:py-10 text-center" data-id="element-95">
            {/* Eyebrow */}
            <p className="font-sans uppercase mb-3" style={{
            color: '#E5C97A',
            fontSize: '11px',
            letterSpacing: '0.28em',
            fontWeight: 600
          }} data-id="element-96">
              El método
            </p>

            {/* Title */}
            <h2 className="font-cinzel mb-4" style={{
            color: '#E5C97A',
            fontSize: 'clamp(24px, 4vw, 34px)',
            fontWeight: 700,
            letterSpacing: '0.03em',
            textShadow: '0 0 18px rgba(229,201,122,0.4)'
          }} data-id="element-97">
              ¿Por qué el Tarot Turco es diferente?
            </h2>

            <div className="w-14 h-px bg-gold/60 mx-auto mb-6" data-id="element-98" />

            {/* Intro */}
            <p className="font-sans mx-auto max-w-2xl mb-8" style={{
            color: '#F5F0E8',
            fontSize: '15.5px',
            lineHeight: 1.7
          }} data-id="element-99">
              El Tarot Turco se trabaja de una manera distinta, a través de{' '}
              <span style={{
              color: '#E5C97A',
              fontWeight: 600
            }} data-id="element-100">
                4 fases
              </span>{' '}
              que permiten interpretar tu situación con más claridad y
              profundidad.
            </p>

            {/* 4 phases grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left mb-7" data-id="element-101">
              <PhaseCard number="01" title="La señal visible" description="Lo que ya está ocurriendo en tu vida." data-id="element-102" />
              <PhaseCard number="02" title="La raíz oculta" description="Eso que todavía no logras ver del todo." data-id="element-103" />
              <PhaseCard number="03" title="El vínculo energético" description="La conexión de tu caso con una persona, emoción o decisión." data-id="element-104" />
              <PhaseCard number="04" title="El camino probable" description="Hacia dónde todo puede moverse si nada cambia." data-id="element-105" />
            </div>

            {/* Closing */}
            <p className="font-cormorant italic mx-auto max-w-2xl" style={{
            color: '#F5F0E8',
            fontSize: '17px',
            lineHeight: 1.6,
            fontWeight: 500
          }} data-id="element-106">
              Por eso la lectura no se siente genérica ni vacía, sino mucho más
              enfocada en{' '}
              <span style={{
              color: '#E5C97A',
              fontWeight: 600
            }} data-id="element-107">
                tu caso real
              </span>
              .
            </p>
          </div>

          {/* Bottom accent line */}
          <div className="absolute bottom-0 left-0 w-full h-[2px]" style={{
          background: 'linear-gradient(90deg, transparent 0%, #E5C97A 50%, transparent 100%)'
        }} data-id="element-108" />
        </div>
      </div>

      {/* ============ DELIVERABLES / "QUÉ RECIBIRÉ" ============ */}
      <div className="mb-16" data-id="element-109">
        {/* Section heading */}
        <div className="text-center mb-10" data-id="element-110">
          <h2 className="font-cinzel mb-3" style={{
          color: '#D4AF37',
          fontSize: 'clamp(26px, 4vw, 38px)',
          fontWeight: 700,
          letterSpacing: '0.03em',
          textShadow: '0 2px 12px rgba(212,175,55,0.2)'
        }} data-id="element-111">
            La claridad que siempre buscaste
          </h2>
          <p className="font-cormorant italic" style={{
          color: '#E0D4BC',
          fontSize: '20px',
          fontWeight: 500
        }} data-id="element-112">
            ¿Qué recibiré exactamente?
          </p>
          <div className="w-16 h-px bg-gold/50 mx-auto mt-5" data-id="element-113" />
        </div>

        {/* Featured intro card */}
        <div className="relative rounded-sm p-6 md:p-8 mb-8 bg-black/60 backdrop-blur-sm" style={{
        border: '1px solid rgba(212,175,55,0.45)',
        boxShadow: '0 0 30px rgba(212,175,55,0.12)'
      }} data-id="element-114">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-70" data-id="element-115" />
          <div className="flex items-start gap-4" data-id="element-116">
            <div className="flex-shrink-0 mt-1" style={{
            color: '#D4AF37'
          }} aria-hidden="true" data-id="element-117">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-118">
                <circle cx="16" cy="16" r="14" stroke="#D4AF37" strokeWidth="1" opacity="0.6" data-id="element-119" />
                <path d="M11 16 L15 20 L21 12" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-120" />
              </svg>
            </div>
            <div className="flex-1" data-id="element-121">
              <h3 className="font-cinzel mb-2" style={{
              color: '#D4AF37',
              fontSize: '19px',
              fontWeight: 600,
              letterSpacing: '0.02em'
            }} data-id="element-122">
                Tu Lectura Personalizada
              </h3>
              <p className="font-sans" style={{
              color: '#F5F0E8',
              fontSize: '15.5px',
              lineHeight: 1.65,
              fontWeight: 400
            }} data-id="element-123">
                Ese nudo que hoy sientes, explicado palabra por palabra para que
                encuentres paz.
              </p>
            </div>
          </div>
        </div>

        {/* Process explanation */}
        <p className="font-sans mx-auto max-w-3xl text-center mb-10" style={{
        color: '#E0D4BC',
        fontSize: '15.5px',
        lineHeight: 1.7
      }} data-id="element-124">
          Al confirmar tu pedido, el Maestro{' '}
          <span style={{
          color: '#D4AF37',
          fontWeight: 600
        }} data-id="element-125">
            Hikmet
          </span>{' '}
          recibe tu perfil y realiza una lectura de cartas de Tarot dedicada
          exclusivamente a tu situación. Él interpretará personalmente cada
          símbolo para poner en palabras las respuestas que hoy no logras ver.
        </p>

        {/* Subsection: ¿Qué recibirás? */}
        <h3 className="font-cinzel text-center mb-6" style={{
        color: '#D4AF37',
        fontSize: '22px',
        fontWeight: 600,
        letterSpacing: '0.03em'
      }} data-id="element-126">
          ¿Qué recibirás en tu correo electrónico?
        </h3>

        <div className="space-y-5" data-id="element-127">
          {/* 1. Lectura personalizada */}
          <DeliverableCard title="Lectura de Tarot Turco Personalizada" body="Recibirás una interpretación escrita de tu tirada de cartas, enfocada en tu situación actual y en la inquietud que hoy más te pesa. Esta lectura te ayudará a entender qué está ocurriendo, qué energía rodea tu caso y qué mensaje necesitas ver con claridad en este momento." data-id="element-128" />

          {/* 2. Bono 1 */}
          <DeliverableCard title="BONO 1: Guía para interpretar tu lectura" badge="Gratis" body="Una guía simple y directa para ayudarte a entender mejor el mensaje de tu lectura. Te mostrará cómo leer cada parte con más claridad, qué prestar atención primero y cómo sacar valor real de lo que recibas, sin sentirte perdida o confundida." data-id="element-129" />

          {/* 3. Bono 2 */}
          <DeliverableCard title="BONO 2: Cómo aplicar tu lectura a tu vida" badge="Gratis" body="No se trata solo de leer una respuesta, sino de saber qué hacer con ella. Este bono te ayudará a llevar el mensaje de tu lectura a tu caso real, a tus decisiones y a tu día a día, para que esa claridad no se quede en teoría." data-id="element-130" />
        </div>

        {/* Delivery time disclaimer */}
        <div className="mt-10 rounded-sm p-5 md:p-6 mx-auto max-w-3xl" style={{
        backgroundColor: 'rgba(212,175,55,0.06)',
        border: '1px solid rgba(212,175,55,0.25)'
      }} data-id="element-131">
          <p className="font-sans text-center" style={{
          color: '#E0D4BC',
          fontSize: '14.5px',
          lineHeight: 1.7
        }} data-id="element-132">
            Para garantizar la precisión de tu análisis, el Maestro requiere un
            tiempo de interpretación manual. Tu lectura de cartas será enviada a
            tu email en un plazo de{' '}
            <span style={{
            color: '#D4AF37',
            fontWeight: 600
          }} data-id="element-133">
              7 días
            </span>
            . Este tiempo es necesario para que Hikmet dedique el enfoque y la
            energía que tu situación merece.
          </p>
        </div>
      </div>

      <OrnamentalDivider className="mb-12" data-id="element-134" />

      {/* Pricing & CTA */}
      <div className="text-center max-w-2xl mx-auto" data-id="element-135">
        {/* "¿Por qué tiene un costo?" small explainer */}
        <div className="mb-6 mx-auto max-w-xl text-left p-5 rounded-sm" style={{
        backgroundColor: 'rgba(212,175,55,0.05)',
        border: '1px solid rgba(212,175,55,0.2)'
      }} data-id="element-136">
          <h5 className="font-cinzel mb-2" style={{
          color: '#D4AF37',
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '0.06em'
        }} data-id="element-137">
            ¿Por qué tiene un costo?
          </h5>
          <p className="font-sans" style={{
          color: '#E0D4BC',
          fontSize: '13.5px',
          lineHeight: 1.65
        }} data-id="element-138">
            Este valor es una forma de contribuir con Hikmet Aydın por el
            tiempo, la atención y la lectura dedicada a tu caso. También
            representa un gesto de agradecimiento por la claridad recibida y por
            la entrega personal que este proceso requiere.
          </p>
        </div>

        <div className="bg-ink/80 border border-gold/40 p-8 rounded-sm shadow-inner-gold mb-8 relative overflow-hidden" data-id="element-139">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold to-transparent" data-id="element-140" />
          <h4 className="font-cinzel text-xl text-gold-light mb-2 uppercase tracking-widest" data-id="element-141">
            Inversión en tu Destino
          </h4>
          <div className="text-5xl font-cinzel text-gold text-shadow-gold font-bold my-4" data-id="element-142">
            $410{' '}
            <span style={{
            fontSize: '24px',
            opacity: 0.7
          }} data-id="element-143">
              MXN
            </span>
          </div>
          <p className="font-cormorant italic mx-auto max-w-md" style={{
          color: '#F5F0E8',
          fontSize: '18px',
          lineHeight: 1.55,
          fontWeight: 500
        }} data-id="element-144">
            El futuro se acerca… <br className="hidden sm:block" data-id="element-145" />y{' '}
            <span style={{
            color: '#e63946',
            fontWeight: 600
          }} data-id="element-146">
              no será amable
            </span>{' '}
            con quienes no estén{' '}
            <span style={{
            color: '#e63946',
            fontWeight: 600
          }} data-id="element-147">
              preparados
            </span>
            .
          </p>
        </div>

        <Button onClick={handlePurchase} variant="primary" fullWidth className="py-6 text-xl shadow-gold-glow-strong btn-ritual-pulse" disabled={isSubmitting} data-id="element-148">
          {isSubmitting ? 'Abriendo el portal...' : 'Quiero conocer mi destino'}
        </Button>

        <div className="mt-8 flex flex-col items-center justify-center gap-1.5 font-sans" style={{
        color: '#4ade80',
        fontWeight: 500
      }} data-id="element-149">
          <div className="flex items-center gap-2" style={{
          fontSize: '14px'
        }} data-id="element-150">
            <ShieldCheck className="w-5 h-5" strokeWidth={1.75} data-id="element-151" />
            <span data-id="element-152">Pago con seguridad bancaria</span>
          </div>
          <span style={{
          fontSize: '13px'
        }} data-id="element-153">
            💳 Único pago, sin suscripciones
          </span>
          <span style={{
          fontSize: '13px'
        }} data-id="element-154">
            ✅ Garantía de devolución de dinero
          </span>
        </div>
      </div>

      {/* ============ ¿DUDAS? / FAQ SECTION ============ */}
      <div className="mt-20 max-w-3xl mx-auto w-full" data-id="element-155">
        {/* "¿Dudas?" intro */}
        <div className="text-center mb-8" data-id="element-156">
          <p className="font-cinzel" style={{
          color: '#D4AF37',
          fontSize: 'clamp(28px, 4vw, 38px)',
          fontWeight: 600,
          letterSpacing: '0.04em'
        }} data-id="element-157">
            ¿Dudas?
          </p>
          <div className="w-16 h-px bg-gold/50 mx-auto my-5" data-id="element-158" />
        </div>

        {/* FAQ container */}
        <div className="relative bg-black border rounded-sm overflow-hidden" style={{
        borderColor: 'rgba(212, 175, 55, 0.35)',
        boxShadow: '0 0 0 1px rgba(212,175,55,0.05), 0 20px 60px rgba(0,0,0,0.5)'
      }} data-id="element-159">
          {/* Top gold hairline accent */}
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" data-id="element-160" />

          <div className="px-5 md:px-8 pt-8 pb-6 text-center" data-id="element-161">
            <h2 className="font-cinzel mb-2" style={{
            color: '#D4AF37',
            fontSize: 'clamp(18px, 2.6vw, 24px)',
            fontWeight: 600,
            letterSpacing: '0.08em'
          }} data-id="element-162">
              RESOLVIENDO TUS DUDAS ANTES DEL ACCESO
            </h2>
            <p className="font-sans mt-3" style={{
            color: '#b8923d',
            fontSize: '12px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            fontWeight: 500
          }} data-id="element-163">
              Preguntas frecuentes verificadas
            </p>
          </div>

          <div className="px-3 md:px-6 pb-6" data-id="element-164">
            <FaqAccordion items={faqItems} data-id="element-165" />
          </div>

          {/* Bottom gold hairline accent */}
          <div className="w-full h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-40" data-id="element-166" />
        </div>
      </div>

      {/* ============ WHATSAPP FLOATING WIDGET (only on offer page) ============ */}
      <a href="https://wa.me/" target="_blank" rel="noopener noreferrer" aria-label="Contactar por WhatsApp" className="fixed z-[60] bottom-6 right-6 md:bottom-8 md:right-8 group" data-id="element-167">
        {/* Pulsing halo */}
        <span aria-hidden="true" className="absolute inset-0 rounded-full" style={{
        background: 'rgba(37, 211, 102, 0.45)',
        animation: 'ritual-pulse 2.4s infinite'
      }} data-id="element-168" />
        <span className="relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full shadow-lg transition-transform duration-300 group-hover:scale-105" style={{
        backgroundColor: '#25D366',
        boxShadow: '0 8px 24px rgba(37, 211, 102, 0.45)'
      }} data-id="element-169">
          <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 md:w-8 md:h-8" fill="white" aria-hidden="true" data-id="element-170">
            <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.521-1.318.214-.46.214-.86.143-1.318-.072-.122-.272-.18-.473-.272-.6-.302-1.366-.602-1.974-.602z" data-id="element-171" />
            <path d="M16.49 6.108c-5.225 0-9.45 4.232-9.45 9.45 0 1.79.495 3.51 1.43 5.014L7 25.84l5.345-1.396c1.46.79 3.107 1.218 4.79 1.218 5.218 0 9.448-4.23 9.448-9.45 0-2.524-.98-4.9-2.764-6.687a9.41 9.41 0 0 0-6.685-2.764zm0 17.32c-1.495 0-2.96-.4-4.245-1.155l-.305-.18-3.165.825.848-3.09-.2-.32a7.85 7.85 0 1 1 14.51-4.19c0 4.34-3.53 7.87-7.87 7.87z" data-id="element-172" />
          </svg>
        </span>
      </a>
    </motion.div>;
};
/* ============================================================
   PHASE CARD — used in the "Por qué el Tarot Turco es diferente" section
============================================================ */
interface PhaseCardProps {
  number: string;
  title: string;
  description: string;
}
const PhaseCard = ({
  number,
  title,
  description
}: PhaseCardProps) => {
  return <div className="relative rounded-sm p-4 md:p-5 transition-all duration-300" style={{
    backgroundColor: 'rgba(0,0,0,0.55)',
    border: '1px solid rgba(212,175,55,0.35)'
  }} data-id="element-173">
      <div className="flex items-start gap-3" data-id="element-174">
        <span className="font-cinzel flex-shrink-0" style={{
        color: '#E5C97A',
        fontSize: '13px',
        fontWeight: 700,
        letterSpacing: '0.2em',
        paddingTop: '2px',
        textShadow: '0 0 10px rgba(229,201,122,0.5)'
      }} data-id="element-175">
          {number}
        </span>
        <div className="flex-1" data-id="element-176">
          <h4 className="font-cinzel mb-1" style={{
          color: '#E5C97A',
          fontSize: '15px',
          fontWeight: 600,
          letterSpacing: '0.02em'
        }} data-id="element-177">
            {title}
          </h4>
          <p className="font-sans" style={{
          color: '#E0D4BC',
          fontSize: '13.5px',
          lineHeight: 1.55
        }} data-id="element-178">
            {description}
          </p>
        </div>
      </div>
    </div>;
};
/* ============================================================
   DELIVERABLE CARD — used in the "Qué recibirás" section
============================================================ */
interface DeliverableCardProps {
  title: string;
  body: string;
  badge?: string;
}
const DeliverableCard = ({
  title,
  body,
  badge
}: DeliverableCardProps) => {
  return <div className="relative rounded-sm p-5 md:p-6 bg-black/55 backdrop-blur-sm" style={{
    border: '1px solid rgba(212,175,55,0.28)'
  }} data-id="element-179">
      <div className="flex items-start gap-4" data-id="element-180">
        {/* Decorative bullet */}
        <div className="flex-shrink-0 mt-1.5 w-2 h-2 rounded-full" style={{
        backgroundColor: '#D4AF37',
        boxShadow: '0 0 8px rgba(212,175,55,0.6)'
      }} aria-hidden="true" data-id="element-181" />

        <div className="flex-1 min-w-0" data-id="element-182">
          <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-183">
            <h4 className="font-cinzel" style={{
            color: '#D4AF37',
            fontSize: '17px',
            fontWeight: 600,
            letterSpacing: '0.02em',
            lineHeight: 1.35
          }} data-id="element-184">
              {title}
            </h4>
            {badge && <span className="font-cinzel uppercase inline-block" style={{
            backgroundColor: '#e63946',
            color: '#FFFFFF',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.18em',
            padding: '3px 10px',
            borderRadius: '2px',
            boxShadow: '0 0 12px rgba(230,57,70,0.45)'
          }} data-id="element-185">
                {badge}
              </span>}
          </div>
          <p className="font-sans" style={{
          color: '#E0D4BC',
          fontSize: '14.5px',
          lineHeight: 1.7,
          fontWeight: 400
        }} data-id="element-186">
            {body}
          </p>
        </div>
      </div>
    </div>;
};